import {
  Timestamp,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  collection,
  Firestore
} from 'firebase/firestore';

export interface RentReviewInput {
  currentRent: number;
  lastSetDate: Date;
  newSetDate: Date;
  capPercentPerYear: number; // e.g. 2
}

export interface RentReviewResult {
  oldHicp: number;
  newHicp: number;
  hicpChangePercent: number; // e.g. 3.7
  capPercentApplied: number; // pro-rata years * cap
  appliedPercent: number; // min of hicpChangePercent and capPercentApplied
  increaseAmount: number; // currentRent * appliedPercent/100
}

export const monthsBetween = (start: Date, end: Date): number => {
  const y = end.getFullYear() - start.getFullYear();
  const m = end.getMonth() - start.getMonth();
  const total = y * 12 + m;
  return Math.max(0, total);
};

export const yearsProRata = (months: number): number => months / 12;

export const calculateRentIncrease = (
  input: RentReviewInput,
  oldHicp: number,
  newHicp: number
): RentReviewResult => {
  const months = monthsBetween(input.lastSetDate, input.newSetDate);
  const years = yearsProRata(months);
  const hicpChangePercent =
    oldHicp > 0 ? ((newHicp - oldHicp) / oldHicp) * 100 : 0;
  const capPercentApplied = input.capPercentPerYear * years;
  const appliedPercent = Math.min(hicpChangePercent, capPercentApplied);
  const increaseAmount = Math.round(input.currentRent * appliedPercent) / 100; // cents precision then divide
  return {
    oldHicp,
    newHicp,
    hicpChangePercent,
    capPercentApplied,
    appliedPercent,
    increaseAmount
  };
};

export const getHicpForDate = async (
  db: Firestore,
  date: Date
): Promise<number | null> => {
  const ts = Timestamp.fromDate(
    new Date(Date.UTC(date.getFullYear(), date.getMonth(), 1))
  );
  const col = collection(db, 'hicpIreland');
  const q = query(
    col,
    where('date', '<=', ts),
    orderBy('date', 'desc'),
    limit(1)
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const val = snap.docs[0].data().value as number;
  return val;
};

// Re-export common date helper for consumers needing consistent month math
export { subtractMonthsSafe } from './dateHelpers';
